import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Moon, Sun } from 'lucide-react';

// internal
import { AuthState, withAuth } from 'context/auth';
import { ProfileMenu, ErrorReportingForm } from 'components';
import { textFocusIn } from 'styles/animations';
import { useColorScheme } from 'context/theme';

const StyledNav = styled.nav`
  padding: 4px 20px;
  background-color: ${p => p.theme.colors.deepGrey};
  display: flex;
`;
const StyledLogo = styled.img`
  display: block;
  vertical-align: middle;
  height: 40px;
  ${textFocusIn};
`;
const StyledLeftSideLinks = styled.div`
  display: flex;
  width: 100%;
  font-size: ${p => p.theme.fontSize.sm};
  margin-right: 16px;
  justify-content: flex-begin;
  a {
    padding-left: 20px;
    line-height: 40px;
    font-weight: bold;
  }
`;
const StyledThemeToggle = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: ${p => p.theme.colors.navFg};
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 6px;
  flex-shrink: 0;
  margin-left: 8px;
  opacity: 0.8;
  transition: opacity 0.2s, background-color 0.2s;

  &:hover {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.12);
  }
`;
const StyledRightSideLinks = styled.div`
  display: flex;
  width: 100%;
  font-size: ${p => p.theme.fontSize.sm};
  margin-right: 16px;
  justify-content: flex-end;
  a {
    padding-left: 20px;
    line-height: 40px;
    font-weight: bold;
    position: relative;
  }
  a.has-tooltip:hover:after {
    content: attr(data-title);
    position: absolute;
    background: ${p => p.theme.colors.lightGrey};
    color: ${p => p.theme.colors.black};
    top: 44px;
    left: 50%;
    transform: translateX(-50%);
    display: inline-block;
    width: 20em;
    line-height: initial;
    padding: 0.5em;
    text-align: center;
    z-index: 100;
  }
  a.has-tooltip:hover:before {
    transform: translateX(-50%);
    content: ' ';
    position: absolute;
    top: 24px;
    left: 50%;
    box-sizing: border-box;
    width: 20px;
    height: 20px;
    border-width: 0 10px 10px 10px;
    border-style: solid;
    border-color: ${p => p.theme.colors.lightGrey} transparent;
  }
`;

interface Props {
  auth: AuthState;
  sandbox: Sandbox | undefined;
}

const Nav = (props: Props) => {
  const [errorFormOpen, setErrorFormOpen] = useState(false);
  const { isDark, toggleTheme } = useColorScheme();

  const renderLeftSideLinks = () => {
    const sandbox = props.auth.sandbox;
    return (
      <StyledLeftSideLinks id="nav-analyst-handbook-link">
        <Link to="https://genes-and-health.github.io/TRE-docs/" target="_blank">
          Documentation
        </Link>
      </StyledLeftSideLinks>
    );
  };

  const renderRightSideLinks = () => {
    const sandbox = props.auth.sandbox;
    if (sandbox === undefined) {
      return null;
    }
    if (!sandbox.termsAccepted) {
      return null;
    }
    return (
      <StyledRightSideLinks id="nav-sandbox-desktop-title">
        <a
          href={`https://console.cloud.google.com/storage/browser/${props.auth.sandbox?.id}_greenuploads`}
          target="_blank"
          className="has-tooltip"
          data-title="This will take you to the google page where you can upload files and folders to the green bucket. Ignore the listing errors, you don't need list permissions to upload to green."
          rel="noreferrer"
        >
          Upload to green bucket
        </a>
        <Link to={`/${sandbox.id}/vm`}>{sandbox.name} Desktop</Link>
      </StyledRightSideLinks>
    );
  };

  const renderProfile = () => {
    if (!props.auth.loggedIn) {
      return null;
    }

    return (
      <ProfileMenu
        currentSandbox={props.auth.sandbox?.id}
        profile={props.auth.userProfile}
        sandboxes={props.auth.sandboxesAvailable}
        onSignOut={() => {
          props.auth.logout(props.auth.userProfile.email);
        }}
        onReportAnError={() => setErrorFormOpen(true)}
        securityExamValidUntil={props.auth.securityExamValidUntil}
        securityExamRequired={props.auth.securityExamRequired}
        trainingSiteURL={props.auth.trainingSiteURL}
      />
    );
  };

  const renderErrorReportingForm = () => {
    if (!props.auth.loggedIn) {
      return null;
    }

    return (
      <ErrorReportingForm
        profile={props.auth.userProfile}
        visible={errorFormOpen}
        onClose={() => setErrorFormOpen(false)}
      />
    );
  };

  const { logo_url = '', title = 'Genes & Health' } =
    props.auth.sandbox?.themeInformation || {};

  const logoDefault = require(`images/${process.env.REACT_APP_LOGO_IMAGE}`);

  return (
    <>
      <StyledNav id="navigation">
        {}
        <a href="/" id="logo-link">
          {!logo_url && <StyledLogo id="logo" src={logoDefault} alt={title} />}
          {logo_url && <StyledLogo id="logo" src={logo_url} alt={title} />}
        </a>
        {renderLeftSideLinks()}
        {renderRightSideLinks()}
        <StyledThemeToggle
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggleTheme}
        >
          {isDark ? <Sun size={18} aria-hidden={true} /> : <Moon size={18} aria-hidden={true} />}
        </StyledThemeToggle>
        {renderProfile()}
      </StyledNav>
      {renderErrorReportingForm()}
    </>
  );
};

export default withAuth(Nav);
